// ─── SiteItemsForm.js ─────────────────────────────────────────────────────────
// Project-level capture screen for counted, non-painting scope items — fixtures
// to supply, fittings to replace, things to remove before painting. Site-wide,
// recorded once per project (not per area). Mobile-first: tap steppers, not
// keyboards, so an assessor one-handed on a phone can move fast.

import React, { useState, useRef, useEffect } from 'react';
import { SITE_ITEMS, SITE_ITEM_STATUS } from './areaConstants';
import { uploadPhoto } from './areaFormUtils';
import ConfirmModal from './ConfirmModal';
import { showToast } from './Toast';

function newRowId() {
  return `${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function emptyRowFor(item) {
  const row = { id: newRowId() };
  item.cols.forEach(c => { row[c.k] = ''; });
  return row;
}

// ── Status segmented control ─────────────────────────────────────────────────
function StatusControl({ value, onChange }) {
  return (
    <div className="si-status">
      {SITE_ITEM_STATUS.map(s => (
        <button key={s.key} type="button"
          className={`si-status-opt ${value === s.key ? 'active' : ''}`}
          onClick={() => onChange(s.key)}>
          {s.label}
        </button>
      ))}
    </div>
  );
}

// ── Three-state "Per client mark-up?" control — never a checkbox, because
// "not stated" is a real, distinct answer in these reports. ────────────────
const MARKUP_OPTIONS = [
  { key: 'yes',      label: 'Yes',        val: true },
  { key: 'no',       label: 'No',         val: false },
  { key: 'unstated', label: 'Not stated', val: null },
];

function MarkupControl({ value, onChange }) {
  return (
    <div className="si-markup">
      <div className="si-markup-label">Per client mark-up?</div>
      <div className="si-status">
        {MARKUP_OPTIONS.map(o => (
          <button key={o.key} type="button"
            className={`si-status-opt ${value === o.val ? 'active' : ''}`}
            onClick={() => onChange(o.val)}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Tap stepper — [ − ] [ value ] [ + ], tap the value for direct numeric entry ──
function CountStepper({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const num = Number(value) || 0;

  const startEdit = () => { setText(String(num)); setEditing(true); };
  const commit = () => {
    const n = parseInt(text, 10);
    onChange(Number.isFinite(n) && n >= 0 ? n : 0);
    setEditing(false);
  };

  return (
    <div className="si-stepper">
      <button type="button" className="si-step-btn" onClick={() => onChange(Math.max(0, num - 1))}>−</button>
      {editing ? (
        <input
          className="si-step-input"
          type="text"
          inputMode="numeric"
          autoFocus
          value={text}
          onChange={e => setText(e.target.value.replace(/[^0-9]/g, ''))}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
        />
      ) : (
        <span className="si-step-val" onClick={startEdit}>{num}</span>
      )}
      <button type="button" className="si-step-btn" onClick={() => onChange(num + 1)}>+</button>
    </div>
  );
}

// ── Repeating row editor for kind:'rows' items ───────────────────────────────
function RowsEditor({ item, rows, onChange }) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const addRow = () => onChange([...safeRows, emptyRowFor(item)]);
  const removeRow = (id) => onChange(safeRows.filter(r => r.id !== id));
  const updateRow = (id, k, v) => onChange(safeRows.map(r => r.id === id ? { ...r, [k]: v } : r));

  return (
    <div className="si-rows">
      {safeRows.map(row => (
        <div key={row.id} className="si-row">
          <div className="si-row-fields">
            {item.cols.map(col => (
              <input key={col.k} className="si-row-input" placeholder={col.label}
                inputMode={col.numeric ? 'numeric' : undefined}
                value={row[col.k] || ''}
                onChange={e => updateRow(row.id, col.k, col.numeric ? e.target.value.replace(/[^0-9]/g, '') : e.target.value)} />
            ))}
          </div>
          <button type="button" className="si-row-remove" onClick={() => removeRow(row.id)} aria-label="Remove row">✕</button>
        </div>
      ))}
      <button type="button" className="si-add-row" onClick={addRow}>＋ Add row</button>
    </div>
  );
}

// ── Photo upload (blinds only) — mirrors the PM photo flow in AssessForm ────
function PhotoUpload({ photos, projectId, onAdd, onUpdate, onRemove }) {
  const cameraRef = useRef(null);
  const libRef = useRef(null);
  const safePhotos = Array.isArray(photos) ? photos : [];

  const handleFile = (file) => {
    if (!file || !projectId) return;
    uploadPhoto(
      file, projectId,
      (tempPhoto) => onAdd({ ...tempPhoto, addedBy: 'pm' }),
      (tempId, { url, thumbnailUrl }) => onUpdate(tempId, { url, thumbnailUrl, pending: false }),
      (tempId) => { showToast('Photo upload failed — please try again', 'error'); onRemove(tempId); }
    );
  };

  return (
    <div className="si-photos">
      {safePhotos.length > 0 && (
        <div className="si-photo-thumbs">
          {safePhotos.map(p => (
            <div key={p.id} className="si-photo-thumb">
              <img src={p.thumbnailUrl || p.url} alt="" />
              {p.pending && <span className="si-photo-pending">Uploading…</span>}
              <button type="button" className="si-photo-remove" onClick={() => onRemove(p.id)} aria-label="Remove photo">✕</button>
            </div>
          ))}
        </div>
      )}
      <div className="si-photo-actions">
        <button type="button" className="si-photo-btn" onClick={() => cameraRef.current?.click()}>📷 Add Photo</button>
        <button type="button" className="si-photo-btn si-photo-lib" onClick={() => libRef.current?.click()}>📁 Library</button>
        <input type="file" accept="image/*" capture="environment" ref={cameraRef} style={{ display: 'none' }}
          onChange={e => { handleFile(e.target.files[0]); e.target.value = ''; }} />
        <input type="file" accept="image/*" ref={libRef} style={{ display: 'none' }}
          onChange={e => { handleFile(e.target.files[0]); e.target.value = ''; }} />
      </div>
    </div>
  );
}

// ── One card per SITE_ITEMS entry ────────────────────────────────────────────
function SiteItemCard({ item, data, onChange, projectId }) {
  const status = data.status || 'counted';
  const showNote = item.kind === 'count' || Object.prototype.hasOwnProperty.call(data, 'note');

  return (
    <div className="si-card">
      <div className="si-card-label">{item.label}</div>
      {item.hint && <div className="si-hint">{item.hint}</div>}

      <StatusControl value={status} onChange={s => onChange({ status: s })} />

      {item.kind === 'count' && status === 'counted' && (
        <CountStepper value={data.count} onChange={n => onChange({ count: n })} />
      )}

      {item.kind === 'rows' && (
        <RowsEditor item={item} rows={data.rows} onChange={rows => onChange({ rows })} />
      )}

      {item.markup && (
        <MarkupControl value={data.perClientMarkup ?? null} onChange={v => onChange({ perClientMarkup: v })} />
      )}

      {showNote && (
        <textarea className="si-note" rows={2} placeholder="Notes"
          value={data.note || ''} onChange={e => onChange({ note: e.target.value })} />
      )}

      {item.photos && (
        <PhotoUpload
          photos={data.photos}
          projectId={projectId}
          onAdd={(p) => onChange({ photos: [...(Array.isArray(data.photos) ? data.photos : []), p] })}
          onUpdate={(tempId, patch) => onChange({
            photos: (Array.isArray(data.photos) ? data.photos : []).map(p => p.id === tempId ? { ...p, ...patch } : p),
          })}
          onRemove={(id) => onChange({ photos: (Array.isArray(data.photos) ? data.photos : []).filter(p => p.id !== id) })}
        />
      )}
    </div>
  );
}

// ── Screen ────────────────────────────────────────────────────────────────────
export default function SiteItemsForm({ project, onSave, onBack }) {
  const [data, setData] = useState(() => project.siteItems || {});
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const dirty = useRef(false);

  useEffect(() => {
    const handler = (e) => { if (dirty.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const updateItem = (key, patch) => {
    dirty.current = true;
    setData(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const updateRequests = (val) => {
    dirty.current = true;
    setData(prev => ({ ...prev, representativeRequests: val }));
  };

  const handleBack = () => {
    if (dirty.current) { setConfirmDiscard(true); return; }
    onBack();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ siteItems: data });
      dirty.current = false;
      showToast('Site items saved', 'success');
    } catch (err) {
      console.error('Site items save failed:', err);
      showToast('Save failed — please try again', 'error');
    }
    setSaving(false);
  };

  return (
    <div className="as-screen">
      {confirmDiscard && (
        <ConfirmModal
          title="Discard changes?"
          message="You have unsaved edits to Site Items. Leaving now will discard them."
          confirmLabel="Discard"
          danger
          onConfirm={() => { dirty.current = false; setConfirmDiscard(false); onBack(); }}
          onCancel={() => setConfirmDiscard(false)}
        />
      )}

      <div className="s2s-header">
        <button className="s2s-back-btn" onClick={handleBack}>← Back</button>
        <div className="s2s-header-center">
          <div className="s2s-header-title">🧰 Site Items</div>
          <div className="s2s-header-sub">{project.projectName || project.clientName || 'Project'}</div>
        </div>
        <div style={{ width: 60 }} />
      </div>

      <div className="as-content" style={{ paddingBottom: 110 }}>
        <div className="si-intro">Counted items recorded during the centre walk-through. Leave anything not applicable as Nil.</div>

        {SITE_ITEMS.map(item => (
          <SiteItemCard
            key={item.key}
            item={item}
            data={data[item.key] || {}}
            onChange={patch => updateItem(item.key, patch)}
            projectId={project.firestoreId}
          />
        ))}

        <div className="si-card">
          <div className="as-section-label">Site representative requests</div>
          <div className="si-hint">Requests from the site contact, outside the audited scope</div>
          <textarea className="si-note" rows={4}
            value={data.representativeRequests || ''}
            onChange={e => updateRequests(e.target.value)} />
        </div>
      </div>

      <div className="si-save-bar">
        <button className="si-save-btn" disabled={saving} onClick={handleSave}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
